import React, { useState } from "react";

//* MUI
import { Stack, Typography } from "@mui/material";

//** ndbx */
import {
  AccordionPanel,
  Section,
  TitleSubTitle,
  TabPanelGroup,
  DataTable,
} from "@arp/react-ndbx";

//! TEST */
// import AccordionPanel from "../../coding/AccordionPanel.jsx";
//! TEST */

//* Config
import { tableHeader } from "../../../config/const/tableHeader.js";
import {
  quoteSummaryDisclaimers,
  quoteSummaryDynamicDisclaimers,
  outcomeDynamicDisclaimers,
} from "../../../config/disclaimers/disclaimers.jsx";
import { Application } from "../../../config/const/enums.js";

const filterOutData = (dataCover, frequency) => {
  if (dataCover == null) return;

  const filteredFrequency = frequency === "monthly" ? "annual" : "monthly";
  const filteredArray = dataCover.map((obj) => ({
    rows: obj.rows.map((row) => ({
      row: row.row.filter((item) => item.frequency !== filteredFrequency),
    })),
  }));
  return filteredArray;
};

//----------------------------------------------------------------
//** Table Tabs */
const createTabItems = (
  showCriticalIllness,
  showPermanentDisability,
  showLifeCover
) => {
  let tabItems = [];

  if (showLifeCover) {
    tabItems.push({ label: "Life", value: 0 });
  }

  if (showCriticalIllness) {
    tabItems.push({ label: "Critical illness", value: 1 });
  }

  if (showPermanentDisability) {
    tabItems.push({ label: "Permanently unable to work", value: 2 });
  }

  return tabItems;
};

//** Accordion Tables */
const createTables = (
  frequency,
  showCriticalIllness,
  showPermanentDisability,
  showLifeCover,
  coverData
) => {
  let tableItems = [];

  //* Filter the data to only show the correct payments
  //* for the select frequency.
  const lifeTable = (
    <DataTable
      label="Life cover projection"
      header={
        frequency === "monthly"
          ? tableHeader().header
          : tableHeader().headerYearly
      }
      tableData={filterOutData(coverData.lifeCover, frequency)}
    />
  );

  if (showLifeCover) {
    tableItems.push(lifeTable);
  }

  const illnessTable = (
    <DataTable
      label="Critical Illness cover projection"
      header={
        frequency === "monthly"
          ? tableHeader().header
          : tableHeader().headerYearly
      }
      tableData={filterOutData(coverData.criticalIllnessCover, frequency)}
    />
  );

  if (showCriticalIllness) {
    tableItems.push(illnessTable);
  }

  const disabilityTable = (
    <DataTable
      label="Permanently Unable to Work cover projection"
      header={
        frequency === "monthly"
          ? tableHeader().header
          : tableHeader().headerYearly
      }
      tableData={filterOutData(coverData.unableToWorkCover, frequency)}
    />
  );

  if (showPermanentDisability) {
    tableItems.push(disabilityTable);
  }

  return tableItems;
};

//** Create the correct components based on screen size */

const createTabPanels = (
  frequency,
  person,
  showCriticalIllness,
  showPermanentDisability,
  showLifeCover,
  coverData
) => {
  return (
    <TabPanelGroup
      sx={{ width: { xs: 380, sm: 618 } }}
      tabItems={createTabItems(
        showCriticalIllness,
        showPermanentDisability,
        showLifeCover
      )}
      panels={createTables(
        frequency,
        showCriticalIllness,
        showPermanentDisability,
        showLifeCover,
        coverData
      )}
    ></TabPanelGroup>
  );
};

const createAccordionPanels = (
  frequency,
  person,
  coverType,
  label,
  tableHeading
) => {
  return (
    <AccordionPanel
      label={label}
      backgroundColor="#ffffff"
      justifyContent="space-between"
      labelVariant="p3-link"
      expanderColor="#006192"
      stackMargin={0}
      // width={{ xs: 358, sm: 616 }}
      // sx={{ width: 618 }}
    >
      {[
        <DataTable
          label={tableHeading}
          header={
            frequency === "monthly"
              ? tableHeader().header
              : tableHeader().headerYearly
          }
          tableData={filterOutData(coverType, frequency)}
        ></DataTable>,
      ]}
    </AccordionPanel>
  );
};

//** This allows us to build any combination of */
//** components as "children" and pass them in to the */
//** Accordion component  */
const children = (frequency, person, application, coverData, parentPage) => {
  const isQuoteSummaryPage = parentPage === "quoteSummaryPage" ? true : false;
  const titleHeader = isQuoteSummaryPage ? "Disclaimer" : "";

  return [
    <TitleSubTitle
      title={
        isQuoteSummaryPage
          ? quoteSummaryDisclaimers.PROVISIONAL_QUOTE_PREMIUM_PROJECTION.title
          : quoteSummaryDisclaimers.PROVISIONAL_QUOTE_PREMIUM_PROJECTION.titleCC
      }
      subtitle={
        isQuoteSummaryPage
          ? quoteSummaryDisclaimers.PROVISIONAL_QUOTE_PREMIUM_PROJECTION.note
          : ""
      }
    ></TitleSubTitle>,
    !isQuoteSummaryPage ? (
      <Section>
        <TitleSubTitle
          title={titleHeader}
          subtitle={
            outcomeDynamicDisclaimers({
              projectionYearlyRate: coverData.projectionYearlyRateDisplay,
            }).disclaimerInfo[person.disclaimerAgeRangeCC].note
          }
        />
      </Section>
    ) : null,

    !isQuoteSummaryPage ? (
      <>
        <Section sx={{ textAlign: "left" }}>
          <Typography variant="p3-bold">{`Would you like more information about the premium projections I
            have discussed?`}</Typography>
        </Section>
        <Section sx={{ textAlign: "center" }}>
          <Typography variant="p3">
            {`<Instruction: next Steps determined by customer response>`}
          </Typography>
        </Section>
      </>
    ) : null,
    <DataTable
      sx={{ width: 540 }}
      label={
        coverData.showLifeCover === true
          ? "Total of covers below"
          : "Life cover projection"
      }
      header={
        frequency === "monthly"
          ? tableHeader(coverData.showLifeCover).header
          : tableHeader(coverData.showLifeCover).headerYearly
      }
      tableData={
        coverData.showLifeCover === true
          ? filterOutData(coverData.totalProjections, frequency)
          : filterOutData(coverData.lifeCover, frequency)
      }
    ></DataTable>,

    <>
      {coverData.showLifeCover === true ? (
        <Section mt={4}>
          <TitleSubTitle
            title="View your individual cover projections below"
            variant={"h5-bold"}
            sx={{ mb: 2 }}
          ></TitleSubTitle>
          <Section sx={{ display: { xs: "none", sm: "flex" } }}>
            {createTabPanels(
              frequency,
              person,
              coverData.isCICover,
              coverData.isPUWCover,
              coverData.showLifeCover,
              coverData
            )}
          </Section>
        </Section>
      ) : null}
      <Section sx={{ display: { xs: "flex", sm: "none" } }}>
        <Stack>
          {/* Life */}
          {coverData.showLifeCover === true
            ? createAccordionPanels(
                frequency,
                person,
                coverData.lifeCover,
                "Life",
                "Life cover projection"
              )
            : null}
          {/* Critical Illness */}
          {coverData.isCICover === true
            ? createAccordionPanels(
                frequency,
                person,
                coverData.criticalIllnessCover,
                "Critical Illness",
                "Critical illness cover projection"
              )
            : null}
          {/* Permanent Disability */}
          {coverData.isPUWCover === true
            ? createAccordionPanels(
                frequency,
                person,
                coverData.unableToWorkCover,
                "Permanently Unable to Work",
                "Permanently Unable to Work cover projection"
              )
            : null}
        </Stack>
      </Section>
      {isQuoteSummaryPage ? (
        <Section>
          <TitleSubTitle
            title="Disclaimer"
            subtitle={
              quoteSummaryDynamicDisclaimers({
                projectionYearlyRate: coverData.projectionYearlyRateDisplay,
              }).disclaimerInfo[person.disclaimerAgeRange].note
            }
          />
        </Section>
      ) : null}
      {/* <Divider /> */}
    </>,
  ];
};

//----------------------------------------------------------------

export default function AccordionTables({ ...props }) {
  const {
    frequency,
    person,
    dataSet,
    premiumData,
    showSecond,
    justifyContent = "space-between",
    stackMargin = { xs: 2, sm: 0, md: 5 },
    parentPage = "quoteSummaryPage",
  } = {
    ...props,
  };

  function isObjEmpty(obj) {
    return Object?.keys(obj)?.length === 0;
  }

  if (isObjEmpty(premiumData) === true) {
    return;
  }

  const [currentPerson] = useState(dataSet[person]);
  const applicantNumber = person === "personOne" ? 1 : 2;
  const applicantIndex = applicantNumber - 1; //Hack, we just need the applicants index.
  const { application } = { ...dataSet };
  const { applicants } = { ...premiumData }; //note-to-self: applicants is an array 0 or 1 index
  const currentApplicant = applicants[applicantIndex][person];
  // const showSecond = true;

  const accordionLabel =
    parentPage === "quoteSummaryPage"
      ? "View your provisional quote premium projection"
      : "View outcome premium projection for applicant";
  return (
    <>
      <AccordionPanel
        justifyContent={justifyContent}
        sx={{ textAlign: "start" }}
        stackMargin={stackMargin}
        width={{ sm: 650 }}
        applicantName={
          showSecond === true
            ? `Applicant ${applicantNumber}: ${currentPerson.firstName} ${currentPerson.surname}`
            : null
        }
        label={accordionLabel}
      >
        {children(
          frequency,
          currentPerson,
          application,
          currentApplicant,
          parentPage
        )}
      </AccordionPanel>
    </>
  );
}
